import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

interface Props {
  /** "city" | "service" | "product" | "latest" */
  type: "city" | "service" | "product" | "latest";
  /** the slug of the city/service/product to load projects for (omit for "latest") */
  slug?: string;
  heading?: string;
  limit?: number;
  className?: string;
}

interface Row {
  project_id: string;
  authority_projects: {
    id: string;
    slug: string;
    title: string;
    summary: string | null;
    hero_image_url: string | null;
    primary_city: string | null;
    completion_date: string | null;
  };
}

/**
 * Embed a collection of authority projects on a city / service / product page.
 * Renders nothing if no published projects exist for the slug.
 */
export default function AuthorityProjectCollection({
  type,
  slug,
  heading,
  limit = 3,
  className = "",
}: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [blockMd, setBlockMd] = useState<string>("");

  useEffect(() => {
    (async () => {
      const linkClient = supabase as any;

      // "latest" mode: just pull the most recently published projects.
      if (type === "latest" || !slug) {
        const { data: projects } = await linkClient
          .from("authority_projects")
          .select(
            "id, slug, title, summary, hero_image_url, primary_city, completion_date",
          )
          .eq("status", "published")
          .order("published_at", { ascending: false })
          .limit(limit);
        const mapped = (projects || []).map((p: any) => ({
          project_id: p.id,
          authority_projects: p,
        }));
        setRows(mapped as any);
        setBlockMd("");
        return;
      }

      const linkTable =
        type === "city"
          ? "authority_project_cities"
          : type === "service"
            ? "authority_project_services"
            : "authority_project_products";
      const slugCol =
        type === "city" ? "city_slug" : type === "service" ? "service_slug" : "product_slug";

      // Fetch link rows first (avoids excessively deep typed-join inference).
      const { data: links } = await linkClient
        .from(linkTable)
        .select(`project_id`)
        .eq(slugCol, slug)
        .limit(limit * 3);

      const projectIds = (links || []).map((l: any) => l.project_id);
      if (projectIds.length) {
        const { data: projects } = await linkClient
          .from("authority_projects")
          .select(
            "id, slug, title, summary, hero_image_url, primary_city, completion_date",
          )
          .in("id", projectIds)
          .eq("status", "published")
          .order("published_at", { ascending: false })
          .limit(limit);

        const mapped = (projects || []).map((p: any) => ({
          project_id: p.id,
          authority_projects: p,
        }));
        setRows(mapped as any);
      } else {
        setRows([]);
      }

      const { data: asset } = await supabase
        .from("authority_project_assets")
        .select("content_md")
        .eq("asset_type", `${type}_block`)
        .eq("target_slug", slug)
        .eq("status", "published")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (asset?.content_md) setBlockMd(asset.content_md);
    })();
  }, [type, slug, limit]);

  if (!rows.length && !blockMd) return null;

  return (
    <section className={`py-12 ${className}`}>
      <div className="container mx-auto px-4">
        {heading && <h2 className="text-3xl font-bold mb-6">{heading}</h2>}
        {blockMd && (
          <div
            className="prose prose-slate max-w-3xl mb-8 text-foreground/90"
            // Plain text rendering of markdown-ish content; whitespace preserved.
          >
            {blockMd.split(/\n{2,}/).map((p, i) =>
              p.trim().startsWith("- ") ? (
                <ul key={i}>
                  {p
                    .split("\n")
                    .filter((l) => l.trim().startsWith("- "))
                    .map((l, j) => (
                      <li key={j}>{l.replace(/^-\s*/, "")}</li>
                    ))}
                </ul>
              ) : (
                <p key={i}>{p}</p>
              ),
            )}
          </div>
        )}
        {rows.length > 0 && (
          <div className="grid gap-6 md:grid-cols-3">
            {rows.map((r) => {
              const p = r.authority_projects;
              return (
                <Link key={r.project_id} to={`/projects/${p.slug}`} className="group">
                  <Card className="overflow-hidden h-full ring-1 ring-accent/10 hover:ring-accent/30 transition">
                    {p.hero_image_url && (
                      <div className="aspect-[16/10] bg-muted overflow-hidden">
                        <img
                          src={p.hero_image_url}
                          alt={`${p.title} project preview`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                        />
                      </div>
                    )}
                    <CardContent className="p-5">
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                        {p.title}
                      </h3>
                      {p.summary && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{p.summary}</p>
                      )}
                      <span className="text-sm text-primary inline-flex items-center gap-1">
                        See project <ArrowRight className="h-4 w-4" />
                        <span className="sr-only">— {p.title}</span>
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
